import { useCallback } from "react";
import { TellescopeModelFromName } from "@tellescope/tanstack-query";
import { FormResponseValueAnswer } from "@tellescope/types-models";
import { convertFormFieldType, deriveComputedValueKey } from "../../utils";
import { TellescopeFieldResponse } from "../../types";
import { useResolveSeededDefault } from "./use-resolve-seeded-default";

export const PUBLIC_IDENTIFIER_SENTINEL = "{{PUBLIC_IDENTIFIER}}";

/**
 * A Hidden Value field carries its value in its title. The sentinel title is substituted with the
 * form's public identifier so a hidden field can capture which link the response came in through.
 *
 * With no identifier to substitute the answer is empty rather than the sentinel itself: the value is
 * submitted and read back as data, so storing `{{PUBLIC_IDENTIFIER}}` would record the template
 * where the missing value belongs.
 */
export const resolveHiddenValue = (title: string | undefined, publicIdentifier: string | undefined): string => {
  if (title === PUBLIC_IDENTIFIER_SENTINEL) return publicIdentifier ?? "";
  return title ?? "";
};

export interface UseSeedEmptyResponseArgs {
  publicIdentifier: string | undefined;
}

/**
 * Returns a builder for the starting response of a field that has no saved, prepopulated, or
 * in-progress answer. The builder returns undefined for display-only types, which hold no value at all.
 */
export const useSeedEmptyResponse = ({ publicIdentifier }: UseSeedEmptyResponseArgs) => {
  const resolveSeededDefault = useResolveSeededDefault();

  return useCallback(
    (field: TellescopeModelFromName<"form_fields">): TellescopeFieldResponse | undefined => {
      const correctType = convertFormFieldType(field.type);
      if (correctType === null) return undefined;

      if (correctType === "Question Group") {
        const answer: FormResponseValueAnswer = {
          type: "Question Group",
          value: field.options?.subFields ?? [],
        };
        return { fieldId: field.id, fieldTitle: field.title, answer };
      }

      if (correctType === "rating") {
        const parsedDefault = field.options?.default !== undefined ? parseInt(String(field.options.default)) : NaN;
        const ratingDefault = Number.isFinite(parsedDefault) ? parsedDefault : undefined;
        // @ts-expect-error Union type gets in the way here
        const answer: FormResponseValueAnswer = { type: field.type, value: ratingDefault };
        return { fieldId: field.id, fieldTitle: field.title, answer };
      }

      if (correctType === "ranking") {
        // @ts-expect-error Union type gets in the way here
        const answer: FormResponseValueAnswer = { type: field.type, value: field.options?.choices ?? [] };
        return { fieldId: field.id, fieldTitle: field.title, answer };
      }

      if (correctType === "Input Table" && !field.isOptional) {
        const emptyRow = (field.options?.tableChoices ?? []).map(({ label }) => ({ label, entry: "" }));
        // @ts-expect-error Union type gets in the way here
        const answer: FormResponseValueAnswer = { type: field.type, value: emptyRow.length ? [emptyRow] : [] };
        return { fieldId: field.id, fieldTitle: field.title, answer };
      }

      if (correctType === "Related Contacts") {
        // @ts-expect-error Union type gets in the way here
        const answer: FormResponseValueAnswer = { type: field.type, value: [] };
        return { fieldId: field.id, fieldTitle: field.title, answer };
      }

      // A Redirect holds no enduser answer, so seed the empty string its answer type requires rather
      // than an absent value. The row is filtered out before it reaches the API, which rejects the
      // empty value — see `isSubmittableResponse`.
      if (correctType === "Redirect") {
        const answer: FormResponseValueAnswer = { type: "Redirect", value: "" };
        return { fieldId: field.id, fieldTitle: field.title, answer };
      }

      if (correctType === "Hidden Value") {
        const answer: FormResponseValueAnswer = {
          type: "Hidden Value",
          value: resolveHiddenValue(field.title, publicIdentifier),
        };
        return { fieldId: field.id, fieldTitle: field.title, answer };
      }

      const seededDefault = resolveSeededDefault(field);
      // @ts-expect-error Union type gets in the way here
      const answer: FormResponseValueAnswer = {
        type: field.type satisfies FormResponseValueAnswer["type"],
        value: seededDefault satisfies FormResponseValueAnswer["value"],
      };
      return {
        fieldId: field.id,
        fieldTitle: field.title,
        answer,
        computedValueKey: deriveComputedValueKey(field.intakeField, answer),
      };
    },
    [publicIdentifier, resolveSeededDefault]
  );
};
